import { useEffect, useMemo, useState } from 'react';

import type { Id, QltClassNote } from '../../api/types';
import { Modal, Spinner } from '../../components';
import { REMINDER_OTHER, REMINDER_PRESETS, type ReminderHomeworkSuggestion } from '../../config/reminder-presets';
import { useQltClassNoteStore, useQltIztContentStore } from '../../stores/qlt';
import { buildParentNotice, classNoteTaskFields, iztCourseKey } from '../../stores/qlt/after-lesson-reminder';
import type { ParentNoticeOutcome } from '../../stores/qlt/class-note';
import { homeworkSuggestions } from './homeworkSuggestions';
import '../../styles/class-management.css';

export type ReminderHomeworkUnit = ReminderHomeworkSuggestion;

export interface ReminderSavedToast {
  kind: 'success' | 'warning';
  message: string;
}

export interface AfterLessonReminderFormProps {
  open: boolean;
  periodLabel: string;
  // the route's period day; board-written note dates can sit a day early
  date?: string;
  subjectTitle?: string;
  lessonName?: string;
  classId: string;
  gradeId?: Id;
  subjectId?: Id;
  onClose: () => void;
  onSaved: (toast: ReminderSavedToast) => void;
}

// parents-notified wording only when POST /posts returned 2xx
function savedToast(outcome: ParentNoticeOutcome): ReminderSavedToast {
  if (outcome.kind === 'sent') return { kind: 'success', message: 'Đã lưu nhắc nhở và gửi thông báo cho phụ huynh' };
  if (outcome.kind === 'unchanged') return { kind: 'success', message: 'Đã lưu nhắc nhở. Nội dung không đổi nên không gửi lại cho phụ huynh' };
  if (outcome.kind === 'unrecorded') return { kind: 'warning', message: outcome.message };
  return { kind: 'success', message: 'Đã lưu nhắc nhở sau tiết học' };
}

function subjectName(note: QltClassNote): string {
  return note.subject && typeof note.subject === 'object' ? note.subject.title || note.subject.name || '' : '';
}

function SparkleIcon() {
  return <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden={true}><path d="M9 1.5 10.6 6 15 7.6 10.6 9.2 9 13.7 7.4 9.2 3 7.6 7.4 6z" fill="currentColor" /><path d="M14.4 12.2 15 13.9l1.7.6-1.7.6-.6 1.7-.6-1.7-1.7-.6 1.7-.6z" fill="currentColor" /></svg>;
}

function DocIcon() {
  return (
    <svg className="cm-homework__icon" width="32" height="32" viewBox="0 0 32 32" aria-hidden={true}>
      <rect width="32" height="32" rx="8" fill="var(--color-primary-soft)" />
      <rect x="10" y="8" width="12" height="16" rx="2.5" fill="none" stroke="var(--color-primary)" strokeWidth="1.5" />
      <path d="M13 13.5h6M13 17h6M13 20.5h3.5" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function AfterLessonReminderForm({ open, periodLabel, date, subjectTitle, lessonName, classId, gradeId, subjectId, onClose, onSaved }: AfterLessonReminderFormProps) {
  const note = useQltClassNoteStore((state) => state.currentNote);
  const loading = useQltClassNoteStore((state) => state.loading);
  const error = useQltClassNoteStore((state) => state.error);
  const saveData = useQltClassNoteStore((state) => state.saveData);
  const notifyParents = useQltClassNoteStore((state) => state.notifyParents);
  const tree = useQltIztContentStore((state) => state.tree);
  const treeLoading = useQltIztContentStore((state) => state.loading);
  const loadTree = useQltIztContentStore((state) => state.loadTree);
  const [presets, setPresets] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [homeworkEnabled, setHomeworkEnabled] = useState(false);
  const [units, setUnits] = useState<ReminderHomeworkUnit[]>([]);
  const [noticeError, setNoticeError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !classId) return;
    void loadTree({ class: classId, grade: gradeId, subject: subjectId });
  }, [open, classId, gradeId, subjectId, loadTree]);

  useEffect(() => {
    if (!open) return;
    const data = note?.data;
    const stored = data && Array.isArray(data.reminderPresets) ? data.reminderPresets : null;
    const legacy = (data?.classReminder || '').trim();
    // no structured fields yet: keep the legacy string visible and editable rather than dropping it
    setPresets(stored || (legacy ? [REMINDER_OTHER] : []));
    setOther(stored ? data?.reminderOther || '' : legacy);
    setHomeworkEnabled(Boolean(data?.homeworkEnabled));
    setUnits(data && Array.isArray(data.homeworkUnits) ? data.homeworkUnits : []);
  }, [note, open]);

  useEffect(() => {
    if (open) setNoticeError(null);
  }, [open]);

  const suggestions = useMemo(() => homeworkSuggestions(tree), [tree]);

  function togglePreset(value: string) {
    setPresets((current) => (current.indexOf(value) >= 0 ? current.filter((item) => item !== value) : current.concat(value)));
  }

  function toggleUnit(unit: ReminderHomeworkUnit) {
    setUnits((current) => (current.some((item) => item.id === unit.id) ? current.filter((item) => item.id !== unit.id) : current.concat(unit)));
  }

  async function save() {
    const otherText = presets.indexOf(REMINDER_OTHER) >= 0 ? other.trim() : '';
    const chosenUnits = homeworkEnabled ? units : [];
    // legacy flat string keeps every existing reader working
    const classReminder = presets.filter((item) => item !== REMINDER_OTHER).concat(otherText ? [otherText] : []).join('; ');
    const homework = chosenUnits.map((item) => item.name).join('; ');
    const task = classNoteTaskFields(chosenUnits, classReminder, iztCourseKey(tree, note?.data));
    setNoticeError(null);
    const updated = await saveData({ reminderPresets: presets, reminderOther: otherText, homeworkEnabled, homeworkUnits: chosenUnits, classReminder, homework, ...task });
    if (!updated) return;
    const notice = buildParentNotice({ subjectTitle: subjectTitle || subjectName(updated), date: date || updated.date, homeworkNames: chosenUnits.map((item) => item.name), reminder: classReminder });
    const outcome = await notifyParents(notice);
    // the note is saved either way; a refused post keeps the form open so Xác nhận retries it
    if (outcome.kind === 'failed') {
      setNoticeError(outcome.message);
      return;
    }
    onSaved(savedToast(outcome));
    onClose();
  }

  const description = `${periodLabel} • Môn ${subjectTitle || 'học'}${lessonName ? `  |  Nội dung:  ${lessonName}` : ''}`;
  const actions = <button type="button" className="cm-btn-primary" disabled={loading || !note} onClick={() => void save()}>{loading ? 'Đang lưu…' : 'Xác nhận'}</button>;

  return (
    <Modal open={open} title="Nhắc nhở sau tiết học" description={description} className="cm-modal cm-modal--meta" actions={actions} onClose={onClose}>
      <div className="cm-label">Nội dung nhắc nhở</div>
      <div className="cm-checklist" role="group" aria-label="Nội dung nhắc nhở">
        {REMINDER_PRESETS.map((preset) => (
          <label className={presets.indexOf(preset) >= 0 ? 'cm-check is-on' : 'cm-check'} key={preset}>
            <input type="checkbox" checked={presets.indexOf(preset) >= 0} onChange={() => togglePreset(preset)} />
            <span>{preset}</span>
          </label>
        ))}
      </div>
      {presets.indexOf(REMINDER_OTHER) >= 0 ? <input className="cm-other" value={other} onChange={(event) => setOther(event.target.value)} placeholder="Nhập nội dung nhắc nhở khác..." aria-label="Nội dung nhắc nhở khác" /> : null}
      <div className="cm-label">Bài tập về nhà</div>
      <label className={homeworkEnabled ? 'cm-check cm-check--solo is-on' : 'cm-check cm-check--solo'}>
        <input type="checkbox" checked={homeworkEnabled} onChange={(event) => setHomeworkEnabled(event.target.checked)} />
        <span>Giao thêm bài tập về nhà</span>
      </label>
      {homeworkEnabled ? (
        <div className="cm-homework">
          <div className="cm-homework__head"><SparkleIcon /> Gợi ý bài tập liên quan</div>
          {treeLoading && suggestions.length === 0 ? <Spinner label="Đang tải bài tập gợi ý" size="small" /> : null}
          {suggestions.length === 0 ? <p className="cm-homework__empty">Chưa có bài tập gợi ý cho bài học này.</p> : null}
          {suggestions.length > 0 ? (
            <div className="cm-homework__list" role="group" aria-label="Bài tập gợi ý">
              {suggestions.map((unit) => {
                const picked = units.some((item) => item.id === unit.id);
                return (
                  <label className={picked ? 'cm-homework__item is-on' : 'cm-homework__item'} key={unit.id}>
                    <input type="checkbox" checked={picked} onChange={() => toggleUnit(unit)} />
                    <DocIcon />
                    <span className="cm-homework__text"><strong>{unit.name}</strong><small>Bài tập về nhà{unit.questionCount === undefined ? '' : ` • ${unit.questionCount} câu hỏi`}</small></span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      {noticeError || error ? <p className="cm-error" role="alert">{noticeError || error}</p> : null}
    </Modal>
  );
}
